import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Users,
  UserPlus,
  Mail,
  Clock,
  MoreVertical,
  Trash2,
  RefreshCw,
  ShieldCheck,
  LogOut,
  Pencil,
  Home,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/Badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/Tabs";
import { useProperty } from "../../contexts/PropertyContext";
import { useAuthStore } from "../../store/authStore";
import {
  getFamilyMembers,
  getPendingInvites,
  getMyInvites,
  cancelInvite,
  resendInvite,
  removeFamilyMember,
  leaveHome,
  FamilyMember,
  FamilyInvite,
  SERVICE_TYPE_LABELS,
  ServiceType,
} from "../../services/family.api";
import { InviteMemberDrawer } from "./InviteMemberDrawer";
import { EditMemberDrawer } from "./EditMemberDrawer";
import { cn } from "../../lib/utils";

const ROLE_BADGE: Record<string, "default" | "secondary" | "outline" | "success" | "warning"> = {
  owner: "default",
  member: "success",
  viewer: "secondary",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function Avatar({ name, url, size = 9 }: { name: string; url?: string; size?: number }) {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className={cn(`h-${size} w-${size} rounded-full object-cover border`)}
      />
    );
  }
  return (
    <div
      className={cn(
        `h-${size} w-${size} rounded-full flex items-center justify-center`,
        "bg-secondary text-muted-foreground text-xs font-medium border"
      )}
    >
      {initials}
    </div>
  );
}

function MemberCard({
  member,
  isCurrentUser,
  onEdit,
  onRemove,
  onLeave,
}: {
  member: FamilyMember;
  isCurrentUser: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onLeave: () => void;
}) {
  const { t } = useTranslation('family');
  const [menuOpen, setMenuOpen] = useState(false);

  const serviceLabels = member.serviceAccess
    .map((s) => SERVICE_TYPE_LABELS[s.serviceType as ServiceType])
    .join(", ");

  return (
    <div className="flex items-start gap-4 p-4 rounded-xl border hover:bg-secondary/30 transition-colors relative">
      <Avatar name={member.user.name} url={member.user.avatarUrl} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium truncate">{member.user.name}</p>
          {isCurrentUser && (
            <span className="text-[10px] text-muted-foreground border rounded-full px-1.5 py-0.5 leading-none">
              {t('member.you')}
            </span>
          )}
          <Badge variant={ROLE_BADGE[member.role] ?? "secondary"} className="text-[10px]">
            {member.role}
          </Badge>
          {member.relation && (
            <span className="text-[10px] text-muted-foreground">{member.relation}</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">{member.user.email}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
          {member.assetAccess.length > 0 && (
            <span className="text-[11px] text-muted-foreground">
              {member.assetAccess.length} {t('member.assetCount', { count: member.assetAccess.length })}
            </span>
          )}
          {member.grantFutureAssets && (
            <span className="text-[11px] text-green-600 dark:text-green-400">{t('member.futureAssets')}</span>
          )}
          {serviceLabels && (
            <span className="text-[11px] text-muted-foreground">{serviceLabels}</span>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">
          {t('members.joined', { date: formatDate(member.joinedAt) })}
        </p>
      </div>

      <div className="relative">
        <button
          onClick={() => setMenuOpen((o) => !o)}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-0 top-8 z-30 w-44 rounded-lg border bg-background shadow-lg py-1">
              {isCurrentUser ? (
                <button
                  onClick={() => { setMenuOpen(false); onLeave(); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-destructive hover:bg-secondary transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  {t('action.leaveHome')}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => { setMenuOpen(false); onEdit(); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-secondary transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    {t('action.editAccess')}
                  </button>
                  <button
                    onClick={() => { setMenuOpen(false); onRemove(); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-destructive hover:bg-secondary transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {t('action.removeMember')}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PendingInviteCard({
  invite,
  onCancel,
  onResend,
  loading,
}: {
  invite: FamilyInvite;
  onCancel: () => void;
  onResend: () => void;
  loading?: boolean;
}) {
  const { t } = useTranslation('family');
  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(invite.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  );

  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border">
      <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
        <Mail className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{invite.email}</p>
        <div className="flex items-center gap-2 flex-wrap mt-0.5">
          <Badge variant={ROLE_BADGE[invite.role] ?? "secondary"} className="text-[10px]">
            {invite.role}
          </Badge>
          {invite.relation && (
            <span className="text-[10px] text-muted-foreground">{invite.relation}</span>
          )}
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <Clock className="h-3 w-3" />
            {t('invite.daysLeft', { n: daysLeft })}
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {t('pending.sent', { date: formatDate(invite.createdAt) })}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onResend}
          disabled={loading}
          title={t('action.resendTitle')}
          className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onCancel}
          disabled={loading}
          title={t('action.cancelTitle')}
          className="p-1.5 rounded-md hover:bg-secondary text-destructive hover:text-destructive transition-colors disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function MyInviteCard({
  invite,
  onAccept,
  onDecline,
}: {
  invite: FamilyInvite;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const { t } = useTranslation('family');
  return (
    <div className="flex items-start gap-4 p-4 rounded-xl border">
      <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {invite.home?.name ?? invite.home?.address ?? t('myInvites.aHome')}
        </p>
        <p className="text-xs text-muted-foreground">
          {invite.home?.city ? `${invite.home.city} — ` : ""}{t('myInvites.invitedAs')}{" "}
          <span className="font-medium text-foreground">{invite.role}</span>
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {t('myInvites.expires', { date: formatDate(invite.expiresAt) })}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="outline" onClick={onDecline}>
          {t('action.decline')}
        </Button>
        <Button size="sm" onClick={onAccept}>
          {t('action.accept')}
        </Button>
      </div>
    </div>
  );
}

export function Family() {
  const { t } = useTranslation('family');
  const { currentProperty } = useProperty();
  const { user } = useAuthStore();

  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<FamilyInvite[]>([]);
  const [myInvites, setMyInvites] = useState<FamilyInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState("");

  const [showInviteDrawer, setShowInviteDrawer] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);

  const homeId = currentProperty?.id;
  const isOwner = currentProperty?.role === "owner" || members.some(
    (m) => m.userId === user?.id && m.role === "owner"
  );

  const load = useCallback(async () => {
    if (!homeId) return;
    setLoading(true);
    setError("");
    try {
      const [membersData, pending, myInv] = await Promise.all([
        getFamilyMembers(homeId),
        getPendingInvites(homeId).catch(() => []),
        getMyInvites().catch(() => []),
      ]);
      setMembers(membersData);
      setPendingInvites(pending);
      setMyInvites(myInv);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [homeId]);

  useEffect(() => { load(); }, [load]);

  const handleRemoveMember = async (member: FamilyMember) => {
    if (!window.confirm(t('confirm.remove', { name: member.user.name }))) return;
    setActionLoading(member.id);
    try {
      await removeFamilyMember(member.id);
      await load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('error.removeFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeaveHome = async (member: FamilyMember) => {
    if (!window.confirm(t('confirm.leave'))) return;
    setActionLoading(member.id);
    try {
      await leaveHome(member.id);
      await load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('error.leaveFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInvite = async (invite: FamilyInvite) => {
    setActionLoading(invite.id);
    try {
      await cancelInvite(invite.id);
      setPendingInvites((p) => p.filter((i) => i.id !== invite.id));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('error.cancelFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendInvite = async (invite: FamilyInvite) => {
    setActionLoading(invite.id);
    try {
      await resendInvite(invite.id);
      alert(t('action.resendSuccess'));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('error.resendFailed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleAcceptMyInvite = (invite: FamilyInvite) => {
    window.location.href = `/invite/accept?token=${invite.token}`;
  };

  const handleDeclineMyInvite = (invite: FamilyInvite) => {
    window.location.href = `/invite/accept?token=${invite.token}&action=decline`;
  };

  if (!homeId) {
    return (
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 text-center">
        <p className="text-muted-foreground">{t('noHome')}</p>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-light tracking-tight">{t('title')}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('subtitle')}{" "}
              <span className="text-foreground">{currentProperty?.name ?? "your home"}</span>
            </p>
          </div>
          {isOwner && (
            <Button
              onClick={() => setShowInviteDrawer(true)}
              className="flex items-center gap-2 shrink-0"
            >
              <UserPlus className="h-4 w-4" />
              {t('inviteMember')}
            </Button>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        {/* Overview card - matches app home access overview */}
        {!loading && (
          <div className="flex items-center justify-center gap-6 px-6 py-4 mb-8 rounded-xl bg-foreground text-primary-foreground">
            <div className="flex flex-col items-center gap-1">
              <Home className="h-4 w-4 opacity-70" />
              <span className="text-xl font-bold">1</span>
              <span className="text-[10px] opacity-60 uppercase tracking-wide">Home</span>
            </div>
            <div className="w-px h-10 bg-primary-foreground/20" />
            <div className="flex flex-col items-center gap-1">
              <Users className="h-4 w-4 opacity-70" />
              <span className="text-xl font-bold">{members.length}</span>
              <span className="text-[10px] opacity-60 uppercase tracking-wide">Members</span>
            </div>
            <div className="w-px h-10 bg-primary-foreground/20" />
            <div className="flex flex-col items-center gap-1">
              <Clock className="h-4 w-4 opacity-70" />
              <span className="text-xl font-bold">{pendingInvites.length}</span>
              <span className="text-[10px] opacity-60 uppercase tracking-wide">Pending</span>
            </div>
          </div>
        )}

        <Tabs defaultValue="members">
          <TabsList className="mb-6">
            <TabsTrigger value="members">
              <Users className="h-3.5 w-3.5 mr-1.5" />
              {t('tabs.members')} {!loading && `(${members.length})`}
            </TabsTrigger>
            <TabsTrigger value="pending">
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              {t('tabs.pendingInvites')} {!loading && `(${pendingInvites.length})`}
            </TabsTrigger>
            <TabsTrigger value="my-invites">
              <Mail className="h-3.5 w-3.5 mr-1.5" />
              {t('tabs.myInvitations')} {!loading && `(${myInvites.length})`}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="members">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 rounded-xl border animate-pulse bg-secondary/30" />
                ))}
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-16">
                <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{t('members.empty')}</p>
                {isOwner && (
                  <Button className="mt-4" onClick={() => setShowInviteDrawer(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('members.inviteFirst')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {members.map((m) => (
                  <MemberCard
                    key={m.id}
                    member={m}
                    isCurrentUser={m.userId === user?.id}
                    onEdit={() => setEditingMember(m)}
                    onRemove={() => handleRemoveMember(m)}
                    onLeave={() => handleLeaveHome(m)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="pending">
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="h-16 rounded-xl border animate-pulse bg-secondary/30" />
                ))}
              </div>
            ) : pendingInvites.length === 0 ? (
              <div className="text-center py-16">
                <Mail className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{t('pending.empty')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {pendingInvites.map((inv) => (
                  <PendingInviteCard
                    key={inv.id}
                    invite={inv}
                    loading={actionLoading === inv.id}
                    onCancel={() => handleCancelInvite(inv)}
                    onResend={() => handleResendInvite(inv)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="my-invites">
            {loading ? (
              <div className="space-y-3">
                {[1].map((i) => (
                  <div key={i} className="h-20 rounded-xl border animate-pulse bg-secondary/30" />
                ))}
              </div>
            ) : myInvites.length === 0 ? (
              <div className="text-center py-16">
                <Mail className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">{t('myInvites.empty')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {myInvites.map((inv) => (
                  <MyInviteCard
                    key={inv.id}
                    invite={inv}
                    onAccept={() => handleAcceptMyInvite(inv)}
                    onDecline={() => handleDeclineMyInvite(inv)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </section>

      <InviteMemberDrawer
        open={showInviteDrawer}
        onOpenChange={setShowInviteDrawer}
        onSuccess={() => { setShowInviteDrawer(false); load(); }}
      />
      {editingMember && (
        <EditMemberDrawer
          open={!!editingMember}
          member={editingMember}
          onOpenChange={(open) => { if (!open) setEditingMember(null); }}
          onSuccess={() => { setEditingMember(null); load(); }}
        />
      )}
    </div>
  );
}
